import React from 'react';
import { cva } from 'class-variance-authority';
import { Icon, IconProps } from '../Icon/Icon';
import styles from './Badge.module.css';
import { focusStyleVariants } from '../../utils/focus/focusStyles';

const badge = cva(styles.badge, {
  variants: {
    size: {
      sm: styles['badge--sm'],
      md: styles['badge--md'],
    },
    styleVariant: {
      neutral: styles['badge--neutral'],
      critical: styles['badge--critical'],
      warning: styles['badge--warning'],
      caution: styles['badge--caution'],
      success: styles['badge--success'],
      info: styles['badge--info'],
      brand: styles['badge--brand'],
      sale: styles['badge--sale'],
      white: styles['badge--white'],
    },
  },
  defaultVariants: {
    size: 'md',
    styleVariant: 'neutral',
  },
});

export interface BadgeProps {
  /**
   * The children of the component
   */
  children?: React.ReactNode;
  /**
   * A string representation of the tag's contents, used for accessibility.
   */
  ariaLabel?: string;
  /**
   * The size of the tag
   */
  size?: 'sm' | 'md';
  /**
   * The style variant of the tag
   */
  styleVariant?:
    | 'neutral'
    | 'critical'
    | 'warning'
    | 'caution'
    | 'success'
    | 'info'
    | 'brand'
    | 'sale'
    | 'white';
  /**
   * Whether an icon should display at the start of the Button and icon properties
   */
  iconStartProps?: IconProps;
  /**
   * Whether an icon should display at the end of Button and icon properties
   */
  iconEndProps?: IconProps;
  /**
   * The focus style of the button
   */
  focusStyle?: 'default' | 'white';
  /**
   * @internal
   * Internal className prop for design system use only
   */
  className?: string;
}

export const Badge = ({
  children,
  size = 'md',
  styleVariant,
  ariaLabel,
  iconStartProps,
  iconEndProps,
  focusStyle = 'default',
  className,
  ...props
}: BadgeProps) => {
  // Set default icon props which are overwritten if passed explicity
  const defaultIconProps: Partial<IconProps> = {
    iconSize: size === 'md' ? '0.75x' : '0.5x',
    iconPrefix: 'fas',
    padding: 'square',
  };

  return (
    <span
      data-testid="badge"
      className={`${badge({ size, styleVariant })} ${focusStyleVariants({ focusStyle })} ${className || ''}`}
      role="status"
      aria-label={ariaLabel}
      {...props}
    >
      {iconStartProps && (
        <Icon {...{ ...defaultIconProps, ...iconStartProps }} />
      )}
      {children}
      {iconEndProps && <Icon {...{ ...defaultIconProps, ...iconEndProps }} />}
    </span>
  );
};

export default Badge;
